/**
 * ItemType_Service.ts
 * Item Type (schema engine) operations  (sk-catalog-api /api/v1/item-types)
 */

import axiosInstance from '../../api/axiosInstance';
import {
  Catalog_ItemTypes_Url,
  Catalog_ItemType_Url,
  Catalog_ItemType_Image_Url,
  Catalog_ItemType_Fields_Url,
  Catalog_ItemType_Field_Url,
} from '../../api/endpoints';
import {
  CreateItemTypePayload,
  UpdateItemTypePayload,
  InitiateTypeImagePayload,
  TypeImagePresignResponse,
  CreateFieldPayload,
  FieldDefinition,
  ItemType,
  ItemTypeResponse,
  ItemTypesResponse,
  FieldsResponse,
} from '../../schemas/catalog/itemType';

/* ─────────────────────────────────────────
   List — GET /api/v1/item-types
───────────────────────────────────────── */

export const getItemTypes = async (): Promise<ItemTypesResponse> => {
  const { data } = await axiosInstance.get<ItemTypesResponse>(Catalog_ItemTypes_Url);
  return data;
};

/* ─────────────────────────────────────────
   Get one — GET /api/v1/item-types/:id
───────────────────────────────────────── */

export const getItemType = async (id: string): Promise<ItemTypeResponse> => {
  const { data } = await axiosInstance.get<ItemTypeResponse>(Catalog_ItemType_Url(id));
  return data;
};

/* ─────────────────────────────────────────
   Create — POST /api/v1/item-types
───────────────────────────────────────── */

export const createItemType = async (payload: CreateItemTypePayload): Promise<ItemTypeResponse> => {
  const { data } = await axiosInstance.post<ItemTypeResponse>(Catalog_ItemTypes_Url, payload);
  return data;
};

/* ─────────────────────────────────────────
   Update — PATCH /api/v1/item-types/:id
   Rename / re-describe / set or clear the image (imageKey: null clears it).
───────────────────────────────────────── */

export const updateItemType = async (
  id: string,
  payload: UpdateItemTypePayload,
): Promise<ItemTypeResponse> => {
  const { data } = await axiosInstance.patch<ItemTypeResponse>(Catalog_ItemType_Url(id), payload);
  return data;
};

/* ─────────────────────────────────────────
   Delete — DELETE /api/v1/item-types/:id
   Refused by the API (409) if catalog items still use the type.
───────────────────────────────────────── */

export const deleteItemType = async (id: string): Promise<void> => {
  await axiosInstance.delete(Catalog_ItemType_Url(id));
};

/* ─────────────────────────────────────────
   Type image — presigned PUT upload
   1) presign  → POST /item-types/image-upload → { url, headers, imageKey }
   2) PUT bytes directly to storage (no API proxy)
   3) caller sends imageKey back in create/update
───────────────────────────────────────── */

export const presignTypeImage = async (
  payload: InitiateTypeImagePayload,
): Promise<TypeImagePresignResponse> => {
  const { data } = await axiosInstance.post<TypeImagePresignResponse>(Catalog_ItemType_Image_Url, payload);
  return data;
};

// High-level helper: presign, PUT the bytes straight to storage, return the
// imageKey to store on the type. `file` is a web File / Blob. Throws on a
// failed PUT so the caller can surface a clear error.
export const uploadTypeImage = async (file: Blob & { name?: string }): Promise<string> => {
  const presigned = await presignTypeImage({
    contentType: file.type || 'application/octet-stream',
    sizeBytes: (file as any).size,
  });
  const res = await fetch(presigned.url, {
    method: 'PUT',
    headers: presigned.headers,
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Image upload failed (${res.status})`);
  }
  return presigned.imageKey;
};

/* ─────────────────────────────────────────
   List fields — GET /api/v1/item-types/:id/fields
───────────────────────────────────────── */

export const getItemTypeFields = async (id: string): Promise<FieldsResponse> => {
  const { data } = await axiosInstance.get<FieldsResponse>(Catalog_ItemType_Fields_Url(id));
  return data;
};

/* ─────────────────────────────────────────
   Add field — POST /api/v1/item-types/:id/fields
───────────────────────────────────────── */

export const addItemTypeField = async (id: string, payload: CreateFieldPayload): Promise<FieldDefinition> => {
  const { data } = await axiosInstance.post<FieldDefinition>(Catalog_ItemType_Fields_Url(id), payload);
  return data;
};

/* Update a field's properties — PATCH /item-types/:id/fields/:fieldKey */
export const updateItemTypeField = async (
  id: string,
  fieldKey: string,
  payload: Partial<{ fieldType: string; isRequired: boolean; isLocationScoped: boolean; isLocalized: boolean; defaultValue: unknown; config: { options?: string[]; unit?: string } | null }>,
): Promise<FieldDefinition> => {
  const { data } = await axiosInstance.patch<FieldDefinition>(Catalog_ItemType_Field_Url(id, fieldKey), payload);
  return data;
};

/* Delete a field definition (and its stored values) — DELETE /item-types/:id/fields/:fieldKey */
export const deleteItemTypeField = async (id: string, fieldKey: string): Promise<void> => {
  await axiosInstance.delete(Catalog_ItemType_Field_Url(id, fieldKey));
};

export type { ItemType };
